/**
 * Where a partner's ledger is addressed from (TMMPDM-0095). A partner has a ledger once its payout
 * account has a pay-api payee: the agreement in force carries 0161's `taxIdStatus`, which is `null`
 * for a partner without one. With no agreement in force (an ended or for-cause affiliate, a reseller
 * between agreements) a ledger may still hold a balance, so it is read anyway and tmm-api's coded 404
 * `PAYEE_NOT_FOUND` says there is no payee. Its payee ref is tmm-api's `"<kind>:<id>"` (`payeeRefOf`).
 */
import { useCallback, useEffect, useState } from 'react';
import { adminApi } from '@/api/tmm/admin';
import { payeeRefOf } from './ledger';
import { PartnerLedgerPanel, type LedgerPart, type PayeeState } from './PartnerLedgerPanel';

/** The agreement in force, as far as the payee goes: `null` when none is in force. */
type InForce = { taxIdStatus?: string | null | undefined } | null;

/**
 * Reads the in-force agreement once per visit. In force: its `taxIdStatus` present means a payee exists,
 * absent or `null` means none. None in force: the ledger is read, and its own answer decides.
 */
function usePayee(
  kind: 'RESELLER' | 'AFFILIATE',
  id: number,
  readInForce: () => Promise<InForce>,
): PayeeState {
  const [token, setToken] = useState(0);
  const [answer, setAnswer] = useState<{ token: number; state: PayeeState } | null>(null);
  const retry = useCallback(() => setToken((n) => n + 1), []);
  useEffect(() => {
    let cancelled = false;
    readInForce()
      .then((inForce) => {
        if (cancelled) return;
        const noPayee =
          inForce !== null && (inForce.taxIdStatus === null || inForce.taxIdStatus === undefined);
        setAnswer({
          token,
          state: noPayee ? { state: 'none' } : { state: 'payee', payeeRef: payeeRefOf(kind, id) },
        });
      })
      .catch((failure: unknown) => {
        if (!cancelled) setAnswer({ token, state: { state: 'failed', failure, onRetry: retry } });
      });
    return () => {
      cancelled = true;
    };
  }, [kind, id, readInForce, token, retry]);
  return answer?.token === token ? answer.state : { state: 'loading' };
}

/** One reseller ledger leaf. Balance and holds is the `?tab=ledger` leaf. */
export function ResellerLedgerLeaf({
  resellerId,
  name,
  part = 'balance',
}: {
  resellerId: number;
  name: string;
  part?: LedgerPart;
}) {
  const read = useCallback(
    () => adminApi.getResellerAgreement(resellerId).then((list): InForce => list.current ?? null),
    [resellerId],
  );
  const payee = usePayee('RESELLER', resellerId, read);
  return (
    <PartnerLedgerPanel
      payee={payee}
      name={name}
      payeeStatusTo={`/partners/${encodeURIComponent(String(resellerId))}?tab=agreement`}
      headingLevel="h2"
      part={part}
    />
  );
}

/** The affiliate page's Ledger section. The payee status is its Agreement section, on the same page. */
export function AffiliateLedgerSection({
  slug,
  affiliateId,
  name,
}: {
  slug: string;
  affiliateId: number;
  name: string;
}) {
  const read = useCallback(
    () =>
      adminApi
        .getAffiliateAgreement(slug, affiliateId)
        .then((list): InForce => list.inForce ?? null),
    [slug, affiliateId],
  );
  const payee = usePayee('AFFILIATE', affiliateId, read);
  return (
    <PartnerLedgerPanel
      payee={payee}
      name={name}
      payeeStatusTo="?tab=agreement"
      headingLevel="h2"
      part="all"
    />
  );
}
